import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTimezoneSelect, allTimezones } from 'react-timezone-select';
import { useAuth } from '../../../utils/Auth';
import API from '../../../API';
import PlanSelector from '../PlanSelector/index.js';
import styles from './CreateOrg.module.css';

const CreateOrg = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userObj, config, orgObj, refreshUserToken, isLoading, setActiveOrgId } = useAuth();
  const { reserveOrgId } = API;

  const [orgName, setOrgName] = useState('');
  const [orgNameValidated, setOrgNameValidated] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const MAX_ORG_NAME_LENGTH = 30;
  const [billingCycle, setBillingCycle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [formattedTimezone, setFormattedTimezone] = useState({});
  const { options } = useTimezoneSelect('original', allTimezones);

  useEffect(() => {
    if (timezone) {
      options.forEach(option => {
        if (option.value === timezone) {
          setFormattedTimezone({ label: option.label, offset: option.offset, value: option.value });
        }
      });
    }
  }, [timezone, options]);

  // Handle Stripe checkout redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const org_id = searchParams.get('org_id');

    // Check if this is a return from Stripe checkout
    if (success === 'true' && canceled !== 'true') {
      // Successful payment - refresh token to get updated org_ids
      setError('');
      setSuccess('Organization created successfully! Redirecting...');

      (async () => {
        try {
          await refreshUserToken();
          
          // Set the active org ID to the newly created org
          if (org_id) {
            setActiveOrgId(org_id);
          }
          
          // Wait 1 second before navigating
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Clear the URL params and navigate to organizations
          navigate('/organizations', { replace: true });
        } catch (err) {
          console.error('Error refreshing user token:', err);
          setError('Payment successful, but there was an issue updating your account. Please refresh the page.');
        }
      })();
    } else if (canceled === 'true') {
      // Payment was canceled - delete the reserved org
      setError('Payment was canceled. Your organization was not created.');
      setOrgNameValidated(false);
      setSelectedPlanId(null);
      setBillingCycle(null);

      // Delete the reserved org
      if (org_id) {
        (async () => {
          try {
            await API.deleteReservedOrg(org_id);
            console.log('Deleted reserved org:', org_id);
          } catch (err) {
            console.error('Error deleting reserved org:', err);
          }
        })();
      }
    }
  }, [location.search, navigate, refreshUserToken, setActiveOrgId]);

  const handleValidateOrgName = useCallback((event) => {
    event.preventDefault();
    const trimmedName = orgName.trim();

    if (!trimmedName) {
      setError('Add an organization name to continue.');
      setSuccess('');
      return;
    }

    if (trimmedName.length > MAX_ORG_NAME_LENGTH) {
      setError(`Organization name must be ${MAX_ORG_NAME_LENGTH} characters or less.`);
      setSuccess('');
      return;
    }

    setError('');
    setSuccess('');
    setOrgNameValidated(true);
  }, [orgName]);

  const handleSelectPlan = useCallback(({ planId, billingCycle }) => {
    setSelectedPlanId(planId);
    setBillingCycle(billingCycle);
  }, []);

  const handleReserveOrgId = useCallback(async (event) => {
    event.preventDefault();

    if (!selectedPlanId || !billingCycle) {
      setError('Please select a plan and billing cycle.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Step 1: Get the Stripe price_id from the selected plan
      const selectedPlan = config?.plans?.[selectedPlanId];
      if (!selectedPlan) {
        throw new Error('Selected plan not found');
      }

      const stripePriceId = selectedPlan?.stripe_prices?.[billingCycle]?.price;
      if (!stripePriceId) {
        throw new Error('Price ID not found for selected plan and billing cycle');
      }

      // Step 2: Reserve the org_id
      const { data: { org_id = "" } } = await reserveOrgId({ org_name: orgName.trim() });
      
      if (!org_id) {
        throw new Error('Failed to reserve organization ID');
      }

      console.log('Reserved org_id:', org_id, 'Plan:', selectedPlanId, 'Billing:', billingCycle, 'Price ID:', stripePriceId);
      
      // Step 3: Create Stripe checkout session
      const checkoutData = {
        org_id,
        org_name: orgName.trim(),
        timezone: orgObj?.timezone || formattedTimezone,
        customer_id: '',
        price_id: stripePriceId,
        uid: userObj?.uid,
        trial: false,
        return_url_context: 'createOrg'
      };

      const checkoutResult = await API.createStripeCheckout(checkoutData);
      console.log('Stripe checkout result:', checkoutResult);

      // Step 4: Redirect to Stripe checkout
      if (checkoutResult?.data?.url) {
        window.location.href = checkoutResult.data.url;
      } else {
        throw new Error('No checkout URL returned from Stripe');
      }

    } catch (error) {
      console.error("Error creating organization:", error);
      setError(error?.response?.data?.message || error?.message || "Could not create, please try again");
      setSubmitting(false);
    }
  }, [reserveOrgId, orgName, selectedPlanId, billingCycle, userObj]);

  // const handleCreateOrg = useCallback(async (event) => {
  //   event.preventDefault();
  //   const trimmedName = orgName.trim();

  //   if (!trimmedName) {
  //     setError('Add an organization name to continue.');
  //     setSuccess('');
  //     return;
  //   }

  //   setSubmitting(true);
  //   setError('');
  //   setSuccess('');

  //   try {
  //     if (createOrg) {
  //       const result = await createOrg({ name: trimmedName, timezone: formattedTimezone });
  //       // Update user token if required
  //       if (result?.requiresTokenRefresh) await refreshUserToken();
  //       // Immediately add the new org to the list and switch to it
  //       if (result?.org_id) {
  //         // Pass the new org_id explicitly to avoid closure/timing issues
  //         // The Firestore listener may not have updated userObj.org_ids yet
  //         const currentOrgIds = userObj?.org_ids || [];
  //         const updatedOrgIds = [...currentOrgIds, result.org_id];
  //         await refreshOrganizations(updatedOrgIds);
          
  //         // Now switch to the new org
  //         setActiveOrgId(result.org_id);
  //       }
  //     }

  //     setSuccess('Organization created!');
  //     setOrgName('');
  //   } catch (err) {
  //     setError(err?.message || 'Unable to create the organization right now.');
  //   } finally {
  //     setSubmitting(false);
  //   }
  // }, [createOrg, refreshOrganizations, orgName, formattedTimezone, refreshUserToken, setActiveOrgId, userObj?.org_ids]);

  // Reset to first step
  const handleBackToNameStep = useCallback(() => {
    setOrgNameValidated(false);
    setSelectedPlanId(null);
    setBillingCycle(null);
    setError('');
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <button className={styles.backButton} onClick={() => navigate('/organizations')}>
          <i className='fas fa-chevron-left'></i> Back
        </button>

        <div className={styles.card}>
          {!orgNameValidated ? (
            <>
              <p className={styles.kicker}>Organization setup</p>
              <h1 className={styles.title}>Create a new organization</h1>
              <p className={styles.subtitle}>
                Give your organization a clear name. We will use this to label your workspace and future invites.
              </p>

              {isLoading ? (
                <div className={styles.meta}>Loading your account…</div>
              ) : (
                <form className={styles.form} onSubmit={handleValidateOrgName}>
                  <div className={styles.inputGroup}>
                    <label className={styles.label} htmlFor="org-name">Organization name</label>
                    <input
                      id="org-name"
                      name="org-name"
                      className={styles.input}
                      value={orgName}
                      onChange={(event) => setOrgName(event.target.value.slice(0, MAX_ORG_NAME_LENGTH))}
                      placeholder="e.g. Northwind Studio"
                      autoComplete="organization"
                      disabled={submitting}
                      maxLength={MAX_ORG_NAME_LENGTH}
                    />
                    <div className={styles.charCount}>
                      {orgName.length}/{MAX_ORG_NAME_LENGTH}
                    </div>
                  </div>

                  <div className={styles.actions}>
                    <button className={styles.primaryButton} type="submit" disabled={submitting}>
                      {submitting ? 'Validating…' : 'Continue'}
                    </button>
                    <div className={styles.meta}>Next, you'll select your plan.</div>
                  </div>

                  {error && <div className={styles.error}>{error}</div>}
                  {success && <div className={styles.success}>{success}</div>}
                </form>
              )}
            </>
          ) : (
            <>
              <form className={styles.form} onSubmit={handleReserveOrgId}>
                <PlanSelector onSelectPlan={handleSelectPlan} />

                <div className={styles.actions}>
                  <button 
                    className={styles.primaryButton} 
                    type="submit" 
                    disabled={submitting || !selectedPlanId}
                  >
                    {submitting ? 'Processing…' : 'Create organization'}
                  </button>
                  <button 
                    type="button"
                    className={styles.secondaryButton}
                    onClick={handleBackToNameStep}
                    disabled={submitting}
                  >
                    ← Change organization name
                  </button>
                  <div className={styles.meta}>We will redirect you to complete payment.</div>
                </div>

                {error && <div className={styles.error}>{error}</div>}
                {success && <div className={styles.success}>{success}</div>}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateOrg;
